import {_} from 'lodash';
import * as ko from "knockout";
import {ErrorTracker} from "../../error.tracker";
import {IMessageData, IObjectData, IOrganization} from '../../index';
import {ISubmitDataOverride} from "./system-permission/index";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {Organization} from "./system-permission/Organization";
import {getOrganizationFromId} from "../../util/misc";
import {global} from "../../appContext";
import {nsOverride} from "../override/nsOverride";
import {nsRoute} from "../../util/route";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import mutableHtml from './mutable.html?raw';

export let template = mutableHtml;

export abstract class viewModel extends ParsedRouteDataDecorator {

    private pv = global.permissionValidator;
    private readonly showOverride = nsOverride.showOverride;
    protected user!: IObjectData | null;
    protected organizations: KnockoutObservableArray<Organization> = ko.observableArray<Organization>([]);
    protected readonly errorTracker: ErrorTracker = global.errorAreas.get('permission.edit');
    protected readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected readonly mainOrg = global.login.selectedOrganization();
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    readonly isOnlyOneOrg: boolean = global.login.organizations().length == 1;

    protected abstract init(): void;

    /*******************************************************************************************************************
     * View helpers
     ******************************************************************************************************************/

    private getAvailableOrganizations(): IOrganization[] {
        return global.login.organizations().filter(o1 => !this.organizations().find(o2 => o2.org.organizationid == o1.organizationid));
    }

    private isDefaultOrganizationIncluded(): boolean {
        return this.organizations().some(o => o.org.organizationid == global.login.selectedOrganizationId());
    }

    /*******************************************************************************************************************
     * View interaction
     ******************************************************************************************************************/

    onDeleteOrganizationClick(o: Organization) {
        const index = this.organizations().indexOf(o);
        this.organizations.remove(o);
        global.snackbarData({
            message: `subject is removed|s:${o.org.organizationame ?? ''}`,
            undo: () => this.organizations.splice(index, 0, o),
        });
    }

    private onAddOrganization(vm: viewModel, e: any) {
        if (e.target.value) {
            const organizationId = e.target.value;
            this.addOrg(organizationId);
        }
    }

    private onAddDefaultOrganizationClick() {
        const organizationId = global.login.selectedOrganizationId();
        this.addOrg(organizationId);
    }

    protected addOrg(organizationId: string | null) {
        if (!organizationId) {
            return;
        }
        const org = new Organization(this, this.createOrganization(organizationId), this.user);
        org.addEmptySystemPermission();
        this.organizations.push(org);
        org.toggle(true);
    }

    private onDiscardChangesClick() {
        this.init();
    }

    protected _getSubmitDataPermission(): IOrganization[] {
        return this.organizations().map((o: Organization) => o.getSubmitDataPermission());
    }

    protected _getSubmitDataOverrides(): ISubmitDataOverride[] {
        return _.flatten(this.organizations().map((o: Organization) => o.getSubmitDataOverrides()));
    }

    /*******************************************************************************************************************
     * View computed / helpers
     ******************************************************************************************************************/

    protected isValid = ko.pureComputed((): boolean => {
        if (!this.isReady()) {
            return false;
        }
        const a = this.organizations().length > 0;
        const b = this.organizations().reduce((prev, o) => o.isValid(this.errorTracker) && prev, true);
        return !!(a && b);
    });

    private isChanged = ko.pureComputed((): boolean => {
        const orgsUser = this.user?.permission;
        const orgsMutable = this.organizations();
        if ((orgsUser?.length ?? 0) == 0 && (orgsMutable.length ?? 0) == 0) {
            return false;
        }
        const a = !orgsUser?.every(o1 => orgsMutable.find(o2 => o1.organizationid == o2.org.organizationid));
        const b = orgsMutable.some(o => o.isChanged());
        return a || b;
    });

    protected numTableColumns(): number {
        return nsOverride.showOverride() ? 8 : 7;
    }

    /*******************************************************************************************************************
     * Miscellaneous
     ******************************************************************************************************************/

    protected createOrganization(organizationId: string) {
        const immutable = getOrganizationFromId(organizationId);
        if (!immutable) {
            throw new Error(`Organization not found: ${organizationId}`);
        }
        const org = _.cloneDeep(immutable);
        org.systempermission = [];
        return org;
    }

    override dispose() {
        this.kosub.unsuscribeAll();
    }
}
